import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LoadingSpinner } from '@/shared/components';
import { documentService } from '../services/document.service';

type DocuSignEvent = 'signing_complete' | 'cancel' | 'decline' | 'exception' | 'fax_pending' | 'session_timeout' | 'ttl_expired' | 'viewing_complete';

export default function OnboardingDocumentsComplete() {
    const [status, setStatus] = useState<'processing' | 'success' | 'cancelled' | 'declined' | 'error'>('processing');
    const [errorMessage, setErrorMessage] = useState('');
    const [searchParams] = useSearchParams();

    useEffect(() => {
        const handleDocuSignReturn = async () => {
            // Parse DocuSign return event from URL
            const event = searchParams.get('event') as DocuSignEvent | null;

            console.log('DocuSign return event:', event);

            // Handle user-cancelled or declined scenarios
            if (event === 'cancel') {
                setStatus('cancelled');
                setErrorMessage('You cancelled the signing process. Your documents have not been signed.');
                return;
            }

            if (event === 'decline') {
                setStatus('declined');
                setErrorMessage('You declined to sign the documents. Please contact support if you have questions.');
                return;
            }

            if (event === 'session_timeout' || event === 'ttl_expired') {
                setStatus('error');
                setErrorMessage('Your signing session expired. Please return to documents and try again.');
                return;
            }

            if (event === 'exception') {
                setStatus('error');
                setErrorMessage('An error occurred during signing. Please try again or contact support.');
                return;
            }

            // For signing_complete or no event (manual flow), show success
            if (event === 'signing_complete') {
                setStatus('success');
                // Try to update document status in the background
                try {
                    await documentService.getMyDocuments();
                } catch (err) {
                    console.error('Error refreshing document status:', err);
                }
                return;
            }

            // No event - check document status
            try {
                await new Promise(resolve => setTimeout(resolve, 2000));
                const response = await documentService.getMyDocuments();

                if (response.success) {
                    const allSigned = response.data.completionPercentage === 100;
                    if (allSigned) {
                        setStatus('success');
                    } else {
                        setStatus('error');
                        setErrorMessage('Some documents are still unsigned. Please close this tab and complete all required documents.');
                    }
                }
            } catch (err: any) {
                console.error('Error checking document status:', err);
                setStatus('error');
                setErrorMessage('Failed to verify document status. Please close this tab and try again.');
            }
        };

        handleDocuSignReturn();
    }, [searchParams]);

    const handleCloseTab = () => {
        window.close();
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
                {status === 'processing' && (
                    <>
                        <LoadingSpinner size="lg" text="Processing your signatures..." />
                        <p className="mt-4 text-gray-600">
                            Please wait while we finalize your onboarding documents.
                        </p>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <span className="text-emerald-600 text-3xl">✓</span>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Signatures Received!</h2>
                        <p className="text-gray-600 mb-6">
                            Your documents have been successfully signed and processed.
                        </p>
                        <button
                            onClick={handleCloseTab}
                            className="w-full px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors mb-4"
                        >
                            Close This Tab
                        </button>
                        <p className="text-sm text-gray-500">
                            Return to the original window and click "Continue to Training" to proceed.
                        </p>
                    </>
                )}

                {status === 'cancelled' && (
                    <>
                        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <span className="text-amber-600 text-3xl">⚠</span>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Signing Cancelled</h2>
                        <p className="text-gray-600 mb-6">{errorMessage}</p>
                        <button
                            onClick={handleCloseTab}
                            className="w-full px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors"
                        >
                            Close This Tab
                        </button>
                        <p className="text-sm text-gray-500 mt-4">
                            Return to the original window to try signing again.
                        </p>
                    </>
                )}

                {status === 'declined' && (
                    <>
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <span className="text-red-600 text-3xl">✕</span>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Documents Declined</h2>
                        <p className="text-gray-600 mb-6">{errorMessage}</p>
                        <div className="space-y-3">
                            <button
                                onClick={handleCloseTab}
                                className="w-full px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors"
                            >
                                Close This Tab
                            </button>
                            <p className="text-sm text-gray-500">
                                If you have concerns about the documents, please contact{' '}
                                <a href="mailto:support@turtleconnector.com" className="text-emerald-600 hover:text-emerald-700">
                                    support@turtleconnector.com
                                </a>
                            </p>
                        </div>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <span className="text-red-600 text-3xl">!</span>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong</h2>
                        <p className="text-red-600 mb-6">{errorMessage}</p>
                        <button
                            onClick={handleCloseTab}
                            className="w-full px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors"
                        >
                            Close This Tab
                        </button>
                        <p className="text-sm text-gray-500 mt-4">
                            Return to the original window and try again.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}
